import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  BarChart, Bar,
} from "recharts";
import { ArrowLeft, TrendingUp, Wallet, AlertTriangle, DollarSign } from "lucide-react";
import Topbar from "@/components/layout/Topbar";
import KpiCard from "@/components/KpiCard";
import ChartCard from "@/components/ChartCard";
import DateRangePicker, { rangeToQueryString } from "@/components/DateRangePicker";
import { api, formatCompactMoney, formatMoney } from "@/lib/api";
import { toast, Toaster } from "sonner";

export default function CompanyDashboard() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [range, setRange] = useState({ months: 12 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const qs = rangeToQueryString(range);
    api
      .get(`/dashboard/company/${companyId}?${qs}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load company"))
      .finally(() => setLoading(false));
  }, [companyId, range]);

  const monthly = useMemo(() => {
    return (data?.monthly ?? []).map((d) => ({
      month: d.month.slice(2),
      Budget: Math.round(d.budget),
      Spend: Math.round(d.spend),
      Revenue: Math.round(d.revenue),
    }));
  }, [data]);

  return (
    <>
      <Toaster position="top-right" richColors />
      <Topbar
        title={data?.company?.name || "Company"}
        subtitle={
          data?.company
            ? `${data.company.erp_system} · ${data.company.industry} · ${data.company.region}`
            : ""
        }
        right={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/companies")}
              data-testid="company-back"
              className="flex h-9 items-center gap-1.5 rounded-sm border border-slate-200 px-3 text-xs text-slate-600 hover:border-navy-600 hover:text-navy-600"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              All companies
            </button>
            <DateRangePicker value={range} onChange={setRange} testId="company-range" />
          </div>
        }
      />

      <div className="space-y-6 p-6 animate-fade-in" data-testid="company-dashboard">
        {loading && <div className="text-sm text-slate-500">Loading company data…</div>}
        {data && (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <KpiCard
                testId="ckpi-revenue"
                tone="navy"
                label="Revenue"
                value={data.kpis.total_revenue}
                icon={TrendingUp}
                money
              />
              <KpiCard
                testId="ckpi-spend"
                label="Spend"
                value={data.kpis.total_spend}
                icon={Wallet}
                money
              />
              <KpiCard
                testId="ckpi-budget"
                label="Budget utilisation"
                value={data.kpis.budget_utilization_pct}
                icon={DollarSign}
                suffix="%"
              />
              <KpiCard
                testId="ckpi-fines"
                label="Fines"
                value={data.kpis.total_fines}
                icon={AlertTriangle}
                money
              />
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              <ChartCard
                title="Budget · Spend · Revenue"
                subtitle="Monthly trend"
                testId="company-trend-chart"
                className="xl:col-span-2"
              >
                <div className="h-[320px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={monthly}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                      <XAxis
                        dataKey="month"
                        tick={{ fill: "#475569", fontSize: 11 }}
                        tickLine={false}
                        axisLine={{ stroke: "#E2E8F0" }}
                      />
                      <YAxis
                        tick={{ fill: "#475569", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(v) => formatCompactMoney(v)}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "#0B1A30",
                          border: "none",
                          borderRadius: 4,
                          color: "#fff",
                          fontSize: 12,
                        }}
                        formatter={(v) => formatMoney(v)}
                      />
                      <Legend wrapperStyle={{ fontSize: 11, color: "#475569" }} />
                      <Line
                        type="monotone"
                        dataKey="Budget"
                        stroke="#0B1A30"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="Spend"
                        stroke="#F28C24"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="Revenue"
                        stroke="#475569"
                        strokeWidth={2}
                        strokeDasharray="4 2"
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>

              <ChartCard
                title="Spend by Category"
                subtitle="Selected period"
                testId="company-spend-by-category"
              >
                <div className="h-[320px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.spend_by_category} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
                      <XAxis
                        type="number"
                        tick={{ fill: "#475569", fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(v) => formatCompactMoney(v)}
                      />
                      <YAxis
                        type="category"
                        dataKey="category"
                        tick={{ fill: "#475569", fontSize: 11 }}
                        tickLine={false}
                        axisLine={{ stroke: "#E2E8F0" }}
                        width={80}
                      />
                      <Tooltip
                        contentStyle={{
                          background: "#0B1A30",
                          border: "none",
                          borderRadius: 4,
                          color: "#fff",
                          fontSize: 12,
                        }}
                        formatter={(v) => formatMoney(v)}
                      />
                      <Bar dataKey="amount" fill="#F28C24" radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </ChartCard>
            </div>

            <div className="rounded-md border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                <div>
                  <div className="font-display text-base font-semibold text-navy-600">
                    Recent Records
                  </div>
                  <div className="text-xs text-slate-500">
                    Latest 25 transactions for this entity
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                      <th className="px-4 py-2.5 text-left">Date</th>
                      <th className="px-4 py-2.5 text-left">Type</th>
                      <th className="px-4 py-2.5 text-left">Category</th>
                      <th className="px-4 py-2.5 text-left">Department</th>
                      <th className="px-4 py-2.5 text-left">ERP</th>
                      <th className="px-4 py-2.5 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_records.map((r) => (
                      <tr
                        key={r.id}
                        className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                      >
                        <td className="px-4 py-2.5 text-slate-500 tabular">
                          {new Date(r.date).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </td>
                        <td className="px-4 py-2.5">
                          <span
                            className={`rounded-sm px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${
                              r.recordType === "REVENUE"
                                ? "bg-emerald-100 text-emerald-700"
                                : r.recordType === "SPEND"
                                ? "bg-orange-100 text-brand-orange"
                                : r.recordType === "BUDGET"
                                ? "bg-navy-50 text-navy-600"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {r.recordType}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-slate-600">{r.category || "—"}</td>
                        <td className="px-4 py-2.5 text-slate-500">{r.department || "—"}</td>
                        <td className="px-4 py-2.5 text-slate-500">{r.sourceERP}</td>
                        <td className="px-4 py-2.5 text-right font-medium tabular text-navy-600">
                          {formatMoney(r.amount, r.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
